<template>
  <div class="sc-intro">
    <Heading
      class="sc-intro__title"
      tag="h2"
      size="lg"
      align="center"
      text-uppercase
      font-weight="ultra"
    >
      {{ content.title }}
    </Heading>
    <ul class="sc-intro__list">
      <li
        v-for="(product, index) in content.productImages"
        :key="`product-image-${index}`"
        class="sc-intro__product"
      >
        <CdnImage
          class="sc-intro__product-image"
          :src="product.src"
          :alt="product.alt"
          lazy
          render-without-figure
        />
      </li>
    </ul>
    <Heading
      class="sc-intro__copy"
      :text="content.copy"
      tag="h3"
      size="md"
      align="center"
      font-weight="regular"
      spaces-bottom
    />
    <LinkButton
      tag="button"
      transparent
      @click="startConfiguration"
    >
      {{ content.cta.label }}
    </LinkButton>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { Heading, LinkButton } from '@i22-td-smarthome/component-library';
import CdnImage from '@/components/cdn-image.vue';

import { gsap } from '@/lib/gsap';

const props = defineProps({
  content: { type: Object, required: true },
  hideIntro: { type: Boolean },
  animateOnViewport: { type: Boolean },
});

const emit = defineEmits(['update:hideIntro']);

const showIntro = computed(() => !props.hideIntro);

function startConfiguration() {
  emit('update:hideIntro', !showIntro.value);
}

function animateProducts() {
  gsap.to('.sc-intro__product', {
    rotation: 0,
    x: 0,
    duration: 1.5,
    delay: 0.25,
    stagger: 0.3,
    alpha: 1,
  });
}

watch(
  () => props.animateOnViewport,
  (val) => {
    if (val) {
      animateProducts();
    }
  },
  { deep: true, immediate: true }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.sc-intro {
  text-align: center;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @include mq($mq-md) {
    padding: 4rem;
  }

  &__title {
    @include mq(false, $mq-sm - 1) {
      @include font-size(36px, 42px);
    }
  }

  &__copy {
    @include font-size(16px, 22px);
    @include mq(375px) {
      @include font-size(18px, 24px);
    }
    @include mq($mq-sm) {
      @include font-size(21px, 28px);
    }
  }

  &__list {
    padding: 2rem 0;
    display: flex;
    justify-content: center;
    list-style: none;
    grid-gap: 1rem;
  }

  &__product {
    opacity: 0;
    transform: translateX(600px) rotateZ(45deg);
    @include mq($mq-sm) {
      padding: 1rem;
    }
  }

  &__product-image {
    max-width: 280px;
    aspect-ratio: 1 / 1;
    width: 100%;
  }
}
</style>
